import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import app from "./firebase";
import LoadingComponent from "./LoadingComponent";
import TranslateDropdown, { calcCredits } from "./components/TranslateDropdown";
import { useAuth } from "./context/AuthContext";

export default function PdfPage() {
    const [pdfFile, setPdfFile] = useState(null);
    const [format, setFormat] = useState("qna");
    const [translateTo, setTranslateTo] = useState("");
    const [numQuestions, setNumQuestions] = useState(50);
    const [loading, setLoading] = useState(false);
    const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:5000" : "");
    const navigate = useNavigate();
    const { credits, currentUser, refreshCredits } = useAuth();
    const creditCost = calcCredits("pdf", format, translateTo, numQuestions);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith(".pdf")) {
            setPdfFile(file);
        } else {
            alert("Please upload a valid PDF file!");
            setPdfFile(null);
        }
    };

    const handleFormatChange = (e) => setFormat(e.target.value);

    const handlePdfProcess = async () => {
        if (!pdfFile) return alert("Please upload a PDF file first!");

        setLoading(true);

        try {
            const auth = getAuth(app);
            const user = auth.currentUser;
            if (!user) { alert("Please log in to use this feature"); setLoading(false); return; }

            const token = await user.getIdToken();

            const fileBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(pdfFile);
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
            });

            const response = await fetch(`${API_BASE_URL}/api/process-pdf`, {
                method: "POST",
                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    fileData: fileBase64,
                    fileName: pdfFile.name,
                    format,
                    language: "en",
                    translateTo,
                    numQuestions
                }),
            });

            const contentType = response.headers.get("content-type") || "";
            if (!contentType.includes("application/json")) {
                throw new Error(`Server error (${response.status}). The backend may be overloaded. Please try again.`);
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || "Processing failed");
            }

            if (refreshCredits) refreshCredits();

            navigate("/preview", {
                state: {
                    transcript: data.transcript || "No text extracted",
                    qna_result: data.qna_result || [],
                    paragraph_result: data.paragraph_result || "",
                    mcq_result: data.mcq_result || [],
                    fileName: pdfFile.name,
                    format: format,
                },
            });
        } catch (error) {
            console.error("Error:", error);
            alert(`Something went wrong: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container" style={{ textAlign: "center", marginTop: "50px" }}>
            <h1>📄 PDF to Notes Generator</h1>

            {!loading ? (
                <div className="glass-container" style={{ maxWidth: "600px", margin: "0 auto" }}>

                    {currentUser && (
                        <div style={{
                            background: 'linear-gradient(135deg, rgba(255, 154, 158, 0.2), rgba(254, 207, 239, 0.2))',
                            border: '1px solid #ff9a9e',
                            borderRadius: '10px',
                            padding: '15px',
                            marginBottom: '20px',
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#fff'
                        }}>
                            💰 Wallet Balance: {credits !== null ? credits : "—"} Credits
                        </div>
                    )}

                    <div style={{ marginBottom: "20px" }}>
                        <label htmlFor="pdf-file">Upload PDF File:</label>
                        <input
                            id="pdf-file"
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                        />
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                        <label>Output Format:</label>
                        <div className="radio-group" style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input type="radio" value="qna" checked={format === "qna"} onChange={handleFormatChange} />
                                Q&A (10 Comprehensive Pairs)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input type="radio" value="paragraph" checked={format === "paragraph"} onChange={handleFormatChange} />
                                Paragraph (Large)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input type="radio" value="mcq" checked={format === "mcq"} onChange={handleFormatChange} />
                                MCQ
                            </label>
                        </div>
                        {format === "mcq" && (
                            <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem' }}>
                                    Number of Questions: <strong>{numQuestions}</strong>
                                </label>
                                <input
                                    type="range"
                                    min="10"
                                    max="200"
                                    step="10"
                                    value={numQuestions}
                                    onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                                    style={{ width: '100%', accentColor: 'var(--primary-color)' }}
                                />
                                <p style={{ fontSize: '0.75rem', color: numQuestions > 100 ? 'orange' : 'var(--text-muted)', marginTop: '5px' }}>
                                    * Base is 200 credits up to 100 Qs.
                                    {numQuestions > 100 && ` Surcharge: +${numQuestions - 100} extra credits.`}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Translation temporarily disabled for testing */}


                    <button
                        className="btn-primary"
                        onClick={handlePdfProcess}
                        disabled={!pdfFile}
                        style={{ width: "100%", background: "linear-gradient(45deg, #ff9a9e, #fecfef)" }}
                    >
                        Process PDF — {creditCost} Credits
                    </button>
                </div>
            ) : (
                <LoadingComponent />
            )}
        </div>
    );
}
